/* ---- Auth service (token management) ---- */

import type { AuthResult, UserInfo } from '../types';
import { api } from './api.service';

const STORAGE_KEY_ACCESS = 'ga_access_token';
const STORAGE_KEY_REFRESH = 'ga_refresh_token';

class AuthService {
  private currentUser: UserInfo | null = null;

  /** Restore token from localStorage on init */
  init(): boolean {
    const token = localStorage.getItem(STORAGE_KEY_ACCESS);
    if (token) {
      api.setToken(token);
      return true;
    }
    return false;
  }

  isLoggedIn(): boolean {
    return !!api.getToken();
  }

  /** Google OAuth login via server */
  async loginWithGoogle(idToken: string): Promise<void> {
    const result = await api.post<AuthResult>('/auth/google', { id_token: idToken });
    this.saveTokens(result.access_token, result.refresh_token);
    this.currentUser = {
      google_sub: result.google_sub,
      email: result.email,
      name: result.name,
      device_uuid: null,
      role: result.role as 'user' | 'admin',
      created_at: '',
      updated_at: '',
    };
  }

  /** PoC dev login via server endpoint */
  async devLogin(): Promise<void> {
    const apiOrigin = import.meta.env.VITE_API_BASE_URL ?? '';
    const res = await fetch(`${apiOrigin}/api/v1/auth/dev-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: 'user' }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error?.message || 'テストログインに失敗しました');
    }

    const data = await res.json();
    this.saveTokens(data.access_token, data.refresh_token || '');
    if (data.user) {
      this.currentUser = {
        google_sub: data.user.google_sub || '',
        email: data.user.email || '',
        name: data.user.name || '',
        device_uuid: data.user.device_uuid || null,
        role: data.user.role || 'user',
        created_at: data.user.created_at || new Date().toISOString(),
        updated_at: data.user.updated_at || new Date().toISOString(),
      };
    }
  }

  /** Refresh the access token */
  async refresh(): Promise<boolean> {
    const rt = localStorage.getItem(STORAGE_KEY_REFRESH);
    if (!rt) return false;
    try {
      const result = await api.post<{ access_token: string; expires_in: number }>('/auth/refresh', {
        refresh_token: rt,
      });
      api.setToken(result.access_token);
      localStorage.setItem(STORAGE_KEY_ACCESS, result.access_token);
      return true;
    } catch {
      this.logout();
      return false;
    }
  }

  /** Fetch current user from server */
  async fetchUser(): Promise<UserInfo> {
    if (this.currentUser) return this.currentUser;
    const user = await api.get<UserInfo>('/users/me');
    this.currentUser = user;
    return user;
  }

  getUser(): UserInfo | null {
    return this.currentUser;
  }

  setUser(user: UserInfo): void {
    this.currentUser = user;
  }

  logout(): void {
    api.setToken(null);
    localStorage.removeItem(STORAGE_KEY_ACCESS);
    localStorage.removeItem(STORAGE_KEY_REFRESH);
    this.currentUser = null;
  }

  private saveTokens(access: string, refresh: string): void {
    api.setToken(access);
    localStorage.setItem(STORAGE_KEY_ACCESS, access);
    localStorage.setItem(STORAGE_KEY_REFRESH, refresh);
  }
}

export const authService = new AuthService();
